import { formGlobals } from './form-globals';

/**
 * Base Class: BaseInput
 * Purpose: Common functionality for all form input classes to reduce duplication.
 * 
 * This class provides:
 * - Common property initialization (wrap, IDs, error/note elements, state)
 * - Shared methods (_dispatch, _setValid, _setInvalid, _setAriaDescribedBy)
 * - AbortController for cleanup
 * 
 * Subclasses should:
 * - Call super(wrap, formIndex, wrapIndex) in constructor
 * - Implement _getAriaElement() to return the element(s) that need aria-describedby
 * - Override _getValidChangeDetail() if they need custom event details
 * - Override _setAriaInvalid() if they need custom aria-invalid handling
 * 
 * @class
 * @extends {EventTarget}
 */
export default class BaseInput extends EventTarget {
  /**
   * @param {HTMLElement} wrap
   * @param {number|null} formIndex
   * @param {number|null} wrapIndex
   */
  constructor(wrap, formIndex = null, wrapIndex = null) {
    super();

    /** @type {HTMLElement} */
    this.wrap = wrap;

    // IDs - subclasses should override inputId if needed
    this.formIndex = formIndex;
    this.wrapIndex = wrapIndex;
    // Ensure formIndex and wrapIndex are numbers, not null, to avoid toString errors
    const safeFormIndex = formIndex ?? 0;
    const safeWrapIndex = wrapIndex ?? 0;
    this.inputId = `form-${safeFormIndex}-input-${safeWrapIndex}`;
    this.errorId = `${this.inputId}-error`;
    this.noteId = null; // set in _initNoteElement

    // UI parts
    this.errorElement = this.wrap.querySelector(formGlobals.errorSelector);
    this.noteElement = null; // set in _initNoteElement

    // State
    this.valid = true;
    this.blurred = false;
    this.required = false;

    // Cleanup
    this._ac = new AbortController();
  }

  /**
   * Initialize note element and ID.
   * Should be called by subclasses after setting up their specific structure.
   * @param {HTMLElement|null} noteElement - The note element (can be null)
   */
  _initNoteElement(noteElement) {
    this.noteElement = noteElement;
    this.noteId = this.noteElement ? `${this.inputId}-note` : null;
    if (this.noteElement && this.noteId) {
      this.noteElement.setAttribute('id', this.noteId);
    }
  }

  /**
   * Initialize error element ID.
   * Should be called by subclasses if they need to set the error ID.
   */
  _initErrorElement() {
    if (this.errorElement) {
      this.errorElement.setAttribute('id', this.errorId);
    }
  }

  /**
   * Get the element(s) that should receive aria-describedby.
   * Subclasses must override this.
   * @returns {HTMLElement|HTMLElement[]|null} - Single element, array of elements, or null
   */
  _getAriaElement() {
    throw new Error('Subclass must implement _getAriaElement()');
  }

  /**
   * Get the detail object for validchange events.
   * Subclasses can override to add custom fields.
   * @returns {object}
   */
  _getValidChangeDetail() {
    return {
      valid: this.valid,
      instance: this
    };
  }

  /**
   * Set aria-invalid attribute on the appropriate element(s).
   * Subclasses can override for custom behavior (e.g., groups setting it on multiple elements).
   * @param {boolean} invalid - Whether to set invalid
   */
  _setAriaInvalid(invalid) {
    const element = this._getAriaElement();
    if (!element) return;

    if (Array.isArray(element)) {
      element.forEach(el => {
        if (invalid) {
          el.setAttribute('aria-invalid', 'true');
        } else {
          el.removeAttribute('aria-invalid');
        }
      });
    } else {
      if (invalid) {
        element.setAttribute('aria-invalid', 'true');
      } else {
        element.removeAttribute('aria-invalid');
      }
    }
  }

  /**
   * Set aria-describedby on the appropriate element(s).
   * @param {{includeNote?:boolean, includeError?:boolean}} opts
   */
  _setAriaDescribedBy({ includeNote = false, includeError = false } = {}) {
    const element = this._getAriaElement();
    if (!element) return;

    const ids = [];
    if (includeNote && this.noteElement && this.noteId) ids.push(this.noteId);
    if (includeError && this.errorElement && this.errorId) ids.push(this.errorId);

    const ariaValue = ids.length ? ids.join(' ') : null;

    if (Array.isArray(element)) {
      element.forEach(el => {
        if (ariaValue) {
          el.setAttribute('aria-describedby', ariaValue);
        } else {
          el.removeAttribute('aria-describedby');
        }
      });
    } else {
      if (ariaValue) {
        element.setAttribute('aria-describedby', ariaValue);
      } else {
        element.removeAttribute('aria-describedby');
      }
    }
  }

  /**
   * Set valid state and update UI/ARIA.
   * Only updates UI if field has been blurred (user has interacted with it).
   */
  _setValid() {
    const prev = this.valid;
    this.valid = true;

    // Only update UI if field has been blurred (user has interacted with it)
    if (this.blurred) {
      this.wrap.classList.add(formGlobals.validClass);
      this.wrap.classList.remove(formGlobals.invalidClass);
      this._setAriaInvalid(false);
      this._setAriaDescribedBy({ includeNote: true, includeError: false });
    }

    if (prev !== this.valid) {
      this._dispatch('validchange', this._getValidChangeDetail());
    }
  }

  /**
   * Set invalid state and update UI/ARIA.
   * Only updates UI if field has been blurred (user has interacted with it).
   */
  _setInvalid() {
    const prev = this.valid;
    this.valid = false;

    // Only update UI if field has been blurred (user has interacted with it)
    if (this.blurred) {
      this.wrap.classList.add(formGlobals.invalidClass);
      this.wrap.classList.remove(formGlobals.validClass);
      this._setAriaInvalid(true);
      this._setAriaDescribedBy({
        includeNote: !!this.noteElement,
        includeError: !!this.errorElement
      });
    }

    if (prev !== this.valid) {
      this._dispatch('validchange', this._getValidChangeDetail());
    }
  }

  /**
   * Dispatch a custom event from the wrap element and the instance itself.
   * @param {string} type - Event name
   * @param {object} detail - Event detail payload
   */
  _dispatch(type, detail) {
    const evt = new CustomEvent(type, {
      detail,
      bubbles: true,
      composed: true
    });
    
    // Dispatch on wrap element (for DOM listeners)
    if (this.wrap) {
      this.wrap.dispatchEvent(evt);
    }
    
    // Also dispatch on the instance itself (for FormManager listeners)
    // This ensures FormManager can listen directly on the input instance
    const instanceEvt = new CustomEvent(type, {
      detail,
      bubbles: false,
      cancelable: false
    });
    this.dispatchEvent(instanceEvt);
  }

  /**
   * Check if input is valid.
   * Subclasses can override, but default implementation uses this.valid.
   * @returns {boolean}
   */
  isValid() {
    return this.valid;
  }

  /**
   * Get current state snapshot.
   * Subclasses should override to provide specific state.
   * @returns {object}
   */
  getState() {
    return {
      valid: this.valid,
      blurred: this.blurred,
      required: this.required,
      id: this.inputId
    };
  }

  /**
   * Force validation now.
   * Subclasses should override to implement validation logic.
   */
  validateNow() {
    // Default: no-op, subclasses should override
  }

  /**
   * Cleanup listeners and references.
   * Subclasses should call super.destroy() and clean up their specific resources.
   */
  destroy() {
    this._ac.abort();
    this.wrap = null;
    this.errorElement = null;
    this.noteElement = null;
  }
}

